import React, { useState } from 'react'
import { Box, Divider } from '@mui/material'
import { styled, useTheme } from '@mui/system'
import { ExpandMore, Home } from '@mui/icons-material'
import type {
  CashAmount,
  FetchDirectorFinancialDetailsResponseTutors,
  ProgramType,
} from '../../../swagger'
import TutorAccordion from './TutorAccordion'
import getLocaleCurrencyForAmount from '../../../utils/getLocaleCurrencyForAmount'
import { useTranslation } from 'react-i18next'
import FinancialAmount from './common/FinancialAmount'
import FinancialLabel from './common/FinancialLabel'

const ProgramContainer = styled(Box)(({ theme }) => ({
  backgroundColor: 'white',
  border: `1px solid ${theme.palette.borderColor.grey}`,
  borderRadius: theme.spacing(1),
  marginBottom: theme.spacing(2),
  overflow: 'hidden',
  marginTop: theme.spacing(1),
}))

const TableHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.5, 2),
  backgroundColor: '#f8f9ff',
  borderBottom: `1px solid ${theme.palette.borderColor.grey}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  '&:hover': {
    backgroundColor: '#f0f2ff',
  },
}))

const NestedTableContent = styled(Box)({
  padding: 2,
  backgroundColor: 'white',
})

interface ProgramAccordionProps {
  program: {
    programType: ProgramType
    tutors: FetchDirectorFinancialDetailsResponseTutors[]
    tutorOwesDirector: CashAmount
    familyOwesTutor: CashAmount
    familyCount: number
    tutorCount: number
    familiesOweDirector: CashAmount
  }
}

const ProgramAccordion: React.FC<ProgramAccordionProps> = ({ program }) => {
  const theme = useTheme()
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState(false)

  const isNotExpandable = program.tutors.length === 0

  const handleClick = () => {
    if (isNotExpandable) return

    setExpanded((prev) => !prev)
  }

  return (
    <ProgramContainer>
      <TableHeader
        onClick={handleClick}
        sx={{
          cursor: isNotExpandable ? 'cursor' : 'pointer',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
          <Home
            sx={{
              mr: 2,
              color: () =>
                theme.palette.program[program.programType.toLowerCase()] ||
                theme.palette.program.foundations,
              fontSize: 20,
            }}
          />
          <Box>
            <Box
              component="span"
              sx={{
                color: (theme) => theme.palette.primary.main,
                fontFamily: 'sweet-sans-pro, sans-serif',
                fontWeight: 700,
                fontSize: '0.875rem',
              }}
            >
              {program.programType}
            </Box>
            <Box
              component="span"
              sx={{
                display: 'block',
                color: (theme) => theme.palette.primary.dark,
                fontFamily: 'plantin, sans-serif',
                fontSize: '0.75rem',
                mt: 0.25,
              }}
            >
              {t(
                'ProgramAccordion.SubHeader.Label',
                '{{tutors}} tutors • {{families}} families',
                {
                  tutors: program.tutorCount,
                  families: program.familyCount,
                }
              )}
            </Box>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, pr: 2 }}>
          <Box sx={{ textAlign: 'right' }}>
            <FinancialLabel sx={{ fontSize: '0.625rem' }}>
              {t(
                'ProgramAccordion.TutorsOweDirector.Label',
                'Tutors Owe Director'
              )}
            </FinancialLabel>
            <FinancialAmount amountType="positive" sx={{ fontSize: '0.75rem' }}>
              {getLocaleCurrencyForAmount(
                program.tutorOwesDirector.amount,
                program.tutorOwesDirector.currencyCode
              )}
            </FinancialAmount>
          </Box>
          <Divider
            orientation="vertical"
            flexItem
            sx={{
              borderColor: (theme) => theme.palette.primary.main,
              opacity: 0.3,
            }}
          />
          <Box sx={{ textAlign: 'right' }}>
            <FinancialLabel sx={{ fontSize: '0.625rem' }}>
              {t(
                'ProgramAccordion.FamiliesOweTutor.Label',
                'Families Owe Tutor'
              )}
            </FinancialLabel>
            <FinancialAmount amountType="positive" sx={{ fontSize: '0.75rem' }}>
              {getLocaleCurrencyForAmount(
                program.familyOwesTutor.amount,
                program.familyOwesTutor.currencyCode
              )}
            </FinancialAmount>
          </Box>
          <Divider
            orientation="vertical"
            flexItem
            sx={{
              borderColor: (theme) => theme.palette.primary.main,
              opacity: 0.3,
            }}
          />
          <Box sx={{ textAlign: 'right' }}>
            <FinancialLabel sx={{ fontSize: '0.625rem' }}>
              {t(
                'ProgramAccordion.FamiliesOweDirector.Label',
                'Families Owe Director'
              )}
            </FinancialLabel>
            <FinancialAmount amountType="positive" sx={{ fontSize: '0.75rem' }}>
              {getLocaleCurrencyForAmount(
                program.familiesOweDirector.amount,
                program.familiesOweDirector.currencyCode
              )}
            </FinancialAmount>
          </Box>
        </Box>

        {!isNotExpandable && (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <ExpandMore
              sx={{
                color: (theme) => theme.palette.primary.main,
                transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.3s',
                fontSize: 20,
              }}
            />
          </Box>
        )}
      </TableHeader>

      {expanded && (
        <NestedTableContent>
          {program.tutors.map((tutor, index) => (
            <TutorAccordion
              tutor={tutor}
              index={index}
              key={`${tutor.firstName}-${tutor.lastName}`}
            />
          ))}
        </NestedTableContent>
      )}
    </ProgramContainer>
  )
}

export default ProgramAccordion
